import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * @type {number | null}
   */
  #submenuHeightFrame = null;

  /**
   * @type {ReturnType<typeof setTimeout>[]}
   */
  #submenuHeightTimers = [];

  /**
   * Largest measured height for the currently active submenu.
   * @type {number}
   */
  #activeSubmenuHeight = 0;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#clearDeactivateTimer();
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
    this.#cleanupQueuedHeightSync();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#activeSubmenuHeight = 0;
    this.#syncActiveSubmenuHeight();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Keep desktop dropdowns centered in the viewport, not just under the nav item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   */
  #positionSubmenu(item, submenu) {
    if (!submenu) return;

    const isDefaultSlot = item.parentElement?.slot === '';

    if (!isDefaultSlot) {
      submenu.style.removeProperty('--submenu-viewport-center-offset');
      return;
    }

    const itemRect = item.getBoundingClientRect();
    const itemCenter = itemRect.left + itemRect.width / 2;
    const viewportCenter = window.innerWidth / 2;
    submenu.style.setProperty('--submenu-viewport-center-offset', `${viewportCenter - itemCenter}px`);
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#clearDeactivateTimer();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    if (item == this.#state.activeItem) {
      this.#queueActiveSubmenuHeightSync();
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#activeSubmenuHeight = 0;

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.#positionSubmenu(item, submenu);

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      this.#observeSubmenuSize(submenu);

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        this.#queueActiveSubmenuHeightSync();
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after initial deferred content has had time to settle.
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 1500);

      this.#queueActiveSubmenuHeightSync();
    }

    const finalHeight = this.#getActiveSubmenuHeight(item, submenu, isDefaultSlot, hasSubmenu);

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    this.#queueActiveSubmenuHeightSync();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      this.#clearDeactivateTimer();
      return;
    }

    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item after a short grace period so the pointer can move
   * from the top-level link into the absolutely positioned submenu.
   * @param {HTMLElement | null} [item]
   */
  #scheduleDeactivate(item = this.#state.activeItem) {
    this.#clearDeactivateTimer();
    this.#deactivateTimer = setTimeout(() => {
      this.#deactivate(item);
    }, 180);
  }

  #clearDeactivateTimer() {
    clearTimeout(this.#deactivateTimer);
    this.#deactivateTimer = undefined;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    const submenu = findSubmenu(item);
    if (submenu?.matches(':hover, :focus-within') || this.overflowListHovered || this.overflowMenu?.matches(':hover')) {
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.#activeSubmenuHeight = 0;
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
    this.#cleanupResizeObserver();
    this.#cleanupQueuedHeightSync();
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Measure the visible height that the header clip needs to reveal.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #getActiveSubmenuHeight(item, submenu, isDefaultSlot, hasSubmenu) {
    if (!submenu) return 0;

    let height = measureMenuHeight(submenu);

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = measureMenuHeight(this.overflowMenu);
        height = Math.max(overflowHeight, overflowListHeight);
      } else {
        height = overflowListHeight;
      }
    }

    if (item === this.#state.activeItem) {
      this.#activeSubmenuHeight = Math.max(this.#activeSubmenuHeight, height);
      return this.#activeSubmenuHeight;
    }

    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Keep the open menu height aligned with its rendered content.
   */
  #syncActiveSubmenuHeight = () => {
    if (!this.#state.activeItem || !this.headerComponent) return;

    const item = this.#state.activeItem;
    const isDefaultSlot = item.parentElement?.slot === '';
    const submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (submenu) {
      this.#positionSubmenu(item, submenu);
    }

    const finalHeight = this.#getActiveSubmenuHeight(item, submenu, isDefaultSlot, hasSubmenu);

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
  };

  /**
   * Remeasure across the first few paint cycles after opening a submenu. Deferred
   * section hydration, image decode, and font swaps can otherwise leave the
   * header clipping to a stale height.
   */
  #queueActiveSubmenuHeightSync() {
    this.#cleanupQueuedHeightSync();

    const syncAfterPaint = () => {
      this.#submenuHeightFrame = requestAnimationFrame(() => {
        this.#submenuHeightFrame = requestAnimationFrame(() => {
          this.#submenuHeightFrame = null;
          this.#syncActiveSubmenuHeight();
        });
      });
    };

    syncAfterPaint();

    for (const delay of [75, 150, 300, 600]) {
      this.#submenuHeightTimers.push(
        setTimeout(() => {
          syncAfterPaint();
        }, delay)
      );
    }

    document.fonts?.ready.then(() => {
      if (this.#state.activeItem) {
        this.#syncActiveSubmenuHeight();
      }
    });
  }

  /**
   * Watch the active submenu for layout changes caused by responsive wrapping or deferred content.
   * @param {HTMLElement} submenu
   */
  #observeSubmenuSize(submenu) {
    this.#cleanupResizeObserver();
    if (!('ResizeObserver' in window)) return;

    this.#submenuResizeObserver = new ResizeObserver(() => {
      this.#queueActiveSubmenuHeightSync();
    });
    this.#submenuResizeObserver.observe(submenu);
    const submenuInner = submenu.querySelector('.menu-list__submenu-inner');
    if (submenuInner instanceof HTMLElement) {
      this.#submenuResizeObserver.observe(submenuInner);
    }
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  #cleanupQueuedHeightSync() {
    if (this.#submenuHeightFrame !== null) {
      cancelAnimationFrame(this.#submenuHeightFrame);
      this.#submenuHeightFrame = null;
    }

    for (const timer of this.#submenuHeightTimers) {
      clearTimeout(timer);
    }
    this.#submenuHeightTimers = [];
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Measure an element's rendered height with a small ceiling buffer for fractional pixels.
 * @param {HTMLElement | null} element
 * @returns {number}
 */
function measureMenuHeight(element) {
  if (!element) return 0;

  return Math.ceil(Math.max(element.offsetHeight, element.getBoundingClientRect().height)) + 2;
}
